import React, { useState } from 'react';
import Icon from '@mdi/react';
import { mdiChevronRight } from '@mdi/js';
import CTAButton from '../../shared/CTAButton';
import { useFadeUp } from '../../shared/useFadeUp';
import {
  workflowBackground,
  mobileCamera,
  mobileObservationOffline,
  mobileResults,
  smartTemplateImport,
  smartTemplateSections,
  smartTemplateNarrative,
} from '../../../assets/v2';
import './workflow.css';

const scrollToDownload = () =>
  document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' });

const STEPS = [
  {
    n: 1,
    title: 'Snap',
    desc: 'Photograph each condition.',
    img: mobileCamera,
  },
  {
    n: 2,
    title: 'Speak',
    desc: 'No typing on a ladder. Works with no signal.',
    img: mobileObservationOffline,
  },
  {
    n: 3,
    title: 'Generate',
    desc: 'Morph writes the full narrative on the spot.',
    img: mobileResults,
  },
];

const TEMPLATE_CARDS = [
  {
    n: 1,
    title: 'Import',
    desc: 'Upload your existing reports. Morph reads your structure, sections, and comment style.',
    img: smartTemplateImport,
  },
  {
    n: 2,
    title: 'Sections',
    desc: 'Your sections and sub-sections are mapped exactly — no rebuilding your inspection structure from scratch.',
    img: smartTemplateSections,
  },
  {
    n: 3,
    title: 'Narrative Library',
    desc: 'Morph builds a library from your own writing and uses it to generate every future report in your voice.',
    img: smartTemplateNarrative,
  },
];

const Workflow: React.FC = () => {
  const title = useFadeUp<HTMLHeadingElement>();
  const copy = useFadeUp<HTMLParagraphElement>();
  const steps = useFadeUp<HTMLDivElement>();
  const tplHeader = useFadeUp<HTMLDivElement>();
  const tplCards = useFadeUp<HTMLDivElement>();
  const tplFooter = useFadeUp<HTMLDivElement>();
  const [activeStep, setActiveStep] = useState<number | null>(null);

  const highlight = (n: number) => setActiveStep(n);
  const clearHighlight = () => setActiveStep(null);

  return (
    <section className="v2-workflow" id="how-it-works" data-page-bg="#0a1628">
      <div className="v2-workflow__top">
        <div className="v2-workflow__visual">
          <div
            className="v2-workflow__env"
            style={{ backgroundImage: `url(${workflowBackground})` }}
          >
            <div className="v2-workflow__vignette" />
            <div className="v2-workflow__phones">
              {STEPS.map((s, i) => (
                <React.Fragment key={s.n}>
                  {i > 0 && (
                    <span className="v2-workflow__arrow" aria-hidden="true">
                      <Icon path={mdiChevronRight} size={1.2} />
                    </span>
                  )}
                  <div
                    className={`v2-workflow__phone ${activeStep === s.n ? 'is-active' : ''}`}
                    onMouseEnter={() => highlight(s.n)}
                    onMouseLeave={clearHighlight}
                    onFocus={() => highlight(s.n)}
                    onBlur={clearHighlight}
                    tabIndex={0}
                    role="img"
                    aria-label={`${s.title} — ${s.desc}`}
                  >
                    <img src={s.img} alt={s.title} loading="lazy" />
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        <div className="v2-workflow__text">
          <h2
            ref={title.ref}
            className={`ui-h2 v2-workflow__title fade-up ${title.isVisible ? 'is-visible' : ''}`}
          >
            Drafted as You Inspect
          </h2>
          <p
            ref={copy.ref}
            className={`ui-body v2-workflow__copy fade-up ${copy.isVisible ? 'is-visible' : ''}`}
          >
            As you capture photos and mark findings, Morph&apos;s AI organizes
            the information and prepares structured report narratives in real
            time. By the end of the walkthrough, most of the report is already
            written — not waiting for you at night.
          </p>
          <div
            ref={steps.ref}
            className={`v2-workflow__steps fade-up ${steps.isVisible ? 'is-visible' : ''}`}
          >
            {STEPS.map((s) => (
              <div
                key={s.n}
                className={`v2-workflow__step ${activeStep === s.n ? 'is-highlight' : ''}`}
                onMouseEnter={() => highlight(s.n)}
                onMouseLeave={clearHighlight}
                onFocus={() => highlight(s.n)}
                onBlur={clearHighlight}
                tabIndex={0}
              >
                <div className="v2-workflow__step-num">{s.n}</div>
                <div className="v2-workflow__step-body">
                  <div className="v2-workflow__step-title">{s.title}</div>
                  <div className="v2-workflow__step-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="v2-workflow__tpl" data-page-bg="#0a1628">
        <div
          ref={tplHeader.ref}
          className={`v2-workflow__tpl-header fade-up ${tplHeader.isVisible ? 'is-visible' : ''}`}
        >
          <h3 className="ui-h2">
            Your voice.
            <br />
            Every report.
          </h3>
          <p className="ui-body">
            Morph doesn&apos;t write for you — it writes like you. Import your
            templates once, and every report Morph generates sounds like it came
            from your keyboard.
          </p>
        </div>

        <div
          ref={tplCards.ref}
          className={`v2-workflow__tpl-cards fade-up ${tplCards.isVisible ? 'is-visible' : ''}`}
        >
          {TEMPLATE_CARDS.map((c) => (
            <div key={c.n} className="v2-workflow__tpl-card">
              <div className="v2-workflow__tpl-thumb">
                <img src={c.img} alt={c.title} loading="lazy" />
              </div>
              <div className="v2-workflow__tpl-body">
                <div className="v2-workflow__step-num">{c.n}</div>
                <div className="v2-workflow__step-title">{c.title}</div>
                <div className="v2-workflow__step-desc">{c.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div
          ref={tplFooter.ref}
          className={`v2-workflow__tpl-footer fade-up ${tplFooter.isVisible ? 'is-visible' : ''}`}
        >
          <p>
            Your writing, not ours. Every narrative Morph generates is built
            from your own inspection&nbsp;history.
          </p>
          <CTAButton variant="blue" size="lg" onClick={scrollToDownload}>
            Run your first job free
          </CTAButton>
        </div>
      </div>
    </section>
  );
};

export default Workflow;
